import React, {useState} from "react";
import {makeStyles, TableCell, TableRow, Typography} from "@material-ui/core";
import Tr from "translations/Translations";
import clsx from "clsx";
import SimpleSelect from "components/SimpleSelect";
import {OfferEvaluationPositionStatus, OfferEvaluationPositionStatusLabel} from "../constants/OfferEvaluationPositionStatus";
import PropTypes from "prop-types";
import ViewMode from "../constants/OfferEvaluationViewMode";
import OffersEvaluationScoreColumn from "./OffersEvaluationScoreColumn";
import Confirm from "components/popup/confirm/Confirm";
import formatAmountNumber from "libs/formatAmountNumber";
import HelpTooltip from "components/HelpTooltip";

const useStyles = makeStyles(theme => ({
    italic: {
        fontStyle: 'italic',
        userSelect: 'none'
    },
    evenRow: {
        backgroundColor: theme.palette.background.default
    },
    lastRow: {
        borderBottom: `solid 2px ${theme.palette.primary.main}`
    },
    scoreColumn: {
        padding: '4px 4px 4px 8px !important'
    },
    statusSelect: {
        width: 170,
        "& .MuiSelect-selectMenu": {
            whiteSpace: "normal"
        }
    },
    withOption: {
        display: 'flex',
        justifyContent: 'space-between'
    }
}));

const statusesList = Object.keys(OfferEvaluationPositionStatus).map(key => ({label: OfferEvaluationPositionStatusLabel(key), value: key}));


function OffersEvaluationRow(
    {
        editable,
        offerEvaluationRow,
        mode,
        rowNumber,
        updateScore,
        updateStatus
    }) {

    const classes = useStyles();
    const even = rowNumber % 2 === 1;
    const [statusWorking, setStatusWorking] = useState(false);
    const [scoreWorking, setScoreWorking] = useState(false);
    const [confirmOpened, setConfirmOpened] = useState(false);

    const onUpdateScore = async (score, criterion) => {
        setScoreWorking(true);
        try {
            await updateScore(score, criterion, offerEvaluationRow);
        } finally {
            setScoreWorking(false);
        }
    }

    const onUpdateStatus = (newStatus, confirmed) => {

        if (!confirmed && newStatus === OfferEvaluationPositionStatus.ALLOWED && offerEvaluationRow.status === OfferEvaluationPositionStatus.EXTREMELY_LOW) {
            setConfirmOpened(true);
        } else {
            setStatusWorking(true);
            updateStatus(newStatus, offerEvaluationRow).finally(() => setStatusWorking(false));
        }

    }

    return (offerEvaluationRow.criteriaEvaluation.map((element, index, array) => {
        const first = index === 0;
        const last = index === array.length - 1;

        const rowSpan = offerEvaluationRow.criteriaEvaluation.length;
        return (<TableRow key={element.identity} className={clsx({[classes.evenRow]: even, [classes.lastRow]: last})}>
            {first && <TableCell className={classes.partColumn} rowSpan={rowSpan}>
                <Typography className={classes.italic} variant="body2">
                    {Tr(`offer_evaluation_mode_title_${mode === ViewMode.SUPPLIER ? ViewMode.POSITION.toLowerCase() : ViewMode.SUPPLIER.toLowerCase()}`, {value: offerEvaluationRow.order})}
                </Typography>
                {mode === ViewMode.POSITION ?
                    <Typography variant="body2">
                        {offerEvaluationRow.consorciumOffer ? Tr('consorcium_with_name', {name: offerEvaluationRow.name}) : offerEvaluationRow.name}
                    </Typography>
                    : <>
                        <Typography variant="body2">
                            {offerEvaluationRow.name}
                        </Typography>
                        {offerEvaluationRow.consorciumOffer && <Typography className={classes.italic} variant="body2">
                            ({Tr('consorcium_offer')})
                        </Typography>}
                    </>

                }
            </TableCell>}
            <TableCell>{element.name}</TableCell>
            <TableCell>{formatAmountNumber(element.weight, '-')}</TableCell>
            <TableCell>
                {!!element.withOption && <div className={classes.withOption}>
                    <span>{element.value}</span>
                    <HelpTooltip text={Tr('offer_value_with_option')} />
                </div>}
                {!element.withOption && element.value}
            </TableCell>
            <TableCell className={classes.scoreColumn}>
                <OffersEvaluationScoreColumn
                    offerEvaluationRow={offerEvaluationRow}
                    offerEvaluationCriterion={element}
                    editable={!statusWorking && editable}
                    updateScore={onUpdateScore}
                />
            </TableCell>

            {first && <TableCell className={classes.scoreTotalColumn} rowSpan={rowSpan}>{formatAmountNumber(offerEvaluationRow.score)}</TableCell>}
            {first && <TableCell className={classes.statusColumn} rowSpan={rowSpan}>
                <SimpleSelect
                    className={classes.statusSelect}
                    disabled={!editable || scoreWorking}
                    items={statusesList}
                    value={offerEvaluationRow.status}
                    onChange={event => onUpdateStatus(event.target.value)}
                    working={statusWorking}
                />
                {<Confirm confirmText='rfx.scoring.offer.extremely.to.allowed.confirm'
                          onCancel={() => setConfirmOpened(false)}
                          onConfirm={() => {
                              setConfirmOpened(false);
                              onUpdateStatus(OfferEvaluationPositionStatus.ALLOWED, true)
                          }}
                          open={confirmOpened}
                />}
            </TableCell>}
        </TableRow>)
    }))
}

OffersEvaluationRow.propTypes = {
    editable: PropTypes.bool.isRequired,
    offerEvaluationRow: PropTypes.object.isRequired,
    mode: PropTypes.oneOf([ViewMode.POSITION, ViewMode.SUPPLIER]).isRequired,
    rowNumber: PropTypes.number.isRequired,
    updateScore: PropTypes.func.isRequired,
    updateStatus: PropTypes.func.isRequired
}

export default OffersEvaluationRow;

